import { RateTableCarrier } from "@redotech/redo-fulfillment-outbound-labels-common/rate-table-carrier";
import {
  AxisKind,
  isOutboundCubicSizeByCountryRateTable,
  isOutboundCubicSizeByZoneRateTable,
  isOutboundWeightByCountryRateTable,
  isOutboundWeightByZoneRateTable,
  isReturnsDefaultWeightByCountryRateTable,
  isReturnsDefaultWeightByZoneRateTable,
  isReturnsTeamWeightByCountryRateTable,
  isReturnsWeightByZoneRateTable,
  OutboundRateTableType,
  RateTable,
  RateTableType,
  RateTableValueMode,
  ReturnsTeamWeightByZoneRateTable,
} from "@redotech/redo-fulfillment-rate-table-common/rate-table";
import { RateTableServiceLevel } from "@redotech/redo-fulfillment-rate-table-common/rate-table-mapping";
import { groupInput, input, InputSchema, listInput } from "@redotech/ui/form";
import { nonEmptyValidator } from "@redotech/ui/validators";

/*
 *  Our rate table input is a grid that looks like this (z = zone, m = max weight, x = rate)
 *     z z z z z z
 *   m x x x x x x
 *   m x x x x x x
 *   m x x x x x x
 *
 *  These functions handle the logic for transforming data to and from this grid from the RateTable model
 */

// The rates are the x's in the grid above - get them from the grid
export function tableDataToRateData(tableData: string[][]): string[][] {
  const data = [...tableData];
  const withoutFirstRow = data.slice(1);
  const withoutFirstColumn = withoutFirstRow.map((row) => row.slice(1));
  return withoutFirstColumn;
}

// The zones are the z's in the grid above - get them from the grid
export function tableDataToZones(tableData: string[][]): string[] {
  const firstRow = tableData.at(0);
  const zones = firstRow?.slice(1);
  return zones ?? [];
}

// Like tableDataToRateData but for country grids that have no x-axis value row
export function tableDataToRateDataNoHeader(tableData: string[][]): string[][] {
  return tableData.map((row) => row.slice(1));
}

// The min weights are the m's in the grid above - get them from the grid
export function tableDataToMinMaxWeights(
  tableData: string[][],
): { minWeight: number; maxWeight: number }[] {
  // Get rid of the zones row
  const withoutFirstRow = tableData.slice(1);
  const minMaxValues = withoutFirstRow.map((row, index) => {
    const prevRow = withoutFirstRow[index - 1];
    const prevMaxWeight = Number(prevRow?.at(0) ?? 0);
    const maxWeight = Number(row.at(0) ?? 0);
    return { minWeight: prevMaxWeight, maxWeight };
  });

  return minMaxValues;
}

// Like tableDataToMinMaxWeights but for tables without a header row (e.g. discount tables)
export function tableDataToMinMaxWeightsNoHeader(
  tableData: string[][],
): { minWeight: number; maxWeight: number }[] {
  return tableData.map((row, index) => {
    const prevRow = tableData[index - 1];
    const prevMaxWeight = Number(prevRow?.at(0) ?? 0);
    const maxWeight = Number(row.at(0) ?? 0);
    return { minWeight: prevMaxWeight, maxWeight };
  });
}

// The max weights are the m's in the grid above - get them from the grid
export function tableDataToMaxValues(tableData: string[][]): string[] {
  return tableData
    .map((row) => row.at(0))
    .filter((value) => value !== undefined);
}

function isReturnsTeamWeightByZoneRateTable(
  rateTable: RateTable | ReturnsTeamWeightByZoneRateTable,
): rateTable is ReturnsTeamWeightByZoneRateTable {
  return rateTable.rateTableType === RateTableType.RETURNS_TEAM_WEIGHT_BY_ZONE;
}

// We only support weight on the y-axis and zone on the x-axis for now
export function rateTableToTableData(rateTable: RateTable): string[][] {
  let maxValues: string[] = [];
  if (
    isOutboundWeightByZoneRateTable(rateTable) ||
    isOutboundWeightByCountryRateTable(rateTable) ||
    isReturnsWeightByZoneRateTable(rateTable) ||
    isReturnsDefaultWeightByZoneRateTable(rateTable) ||
    isReturnsTeamWeightByZoneRateTable(rateTable) ||
    isReturnsDefaultWeightByCountryRateTable(rateTable) ||
    isReturnsTeamWeightByCountryRateTable(rateTable)
  ) {
    maxValues = rateTable.yAxis.values.map((value) =>
      value.maxWeight.toString(),
    );
  } else if (
    isOutboundCubicSizeByZoneRateTable(rateTable) ||
    isOutboundCubicSizeByCountryRateTable(rateTable)
  ) {
    maxValues = rateTable.yAxis.values.map((value) =>
      value.maxCubicSize.toString(),
    );
  }

  const withMaxValues = rateTable.data.map((row, rowIndex) => {
    return [
      maxValues.at(rowIndex) ?? "",
      ...row.map((cell) => cell.toString()),
    ];
  });

  if (rateTable.xAxis?.kind === AxisKind.COUNTRY) {
    return withMaxValues;
  }

  // xAxis may be undefined on yAxis-only variants (e.g. returns_team_by_weight);
  // those variants don't reach this branch, but guard anyway. See INFRA-1157.
  const zones = rateTable.xAxis?.values ?? [];
  // The leftmost cells in the "zone" row are empty
  const zonesWithEmptyCells = ["0", ...zones];
  const zonesAndRates = [zonesWithEmptyCells, ...withMaxValues];

  return zonesAndRates;
}

// The min cubic sizes are the m's in the grid above - get them from the grid
export function tableDataToMinMaxCubicSizes(
  tableData: string[][],
): { minCubicSize: number; maxCubicSize: number }[] {
  // Get rid of the zones row
  const withoutFirstRow = tableData.slice(1);
  const minMaxValues = withoutFirstRow.map((row, index) => {
    const prevRow = withoutFirstRow[index - 1];
    const prevMaxCubicSize = Number(prevRow?.at(0) ?? 0);
    const maxCubicSize = Number(row.at(0) ?? 0);
    return { minCubicSize: prevMaxCubicSize, maxCubicSize };
  });

  return minMaxValues;
}

// Like tableDataToMinMaxCubicSizes but for country grids without a header row
export function tableDataToMinMaxCubicSizesNoHeader(
  tableData: string[][],
): { minCubicSize: number; maxCubicSize: number }[] {
  return tableData.map((row, index) => {
    const prevRow = tableData[index - 1];
    const prevMaxCubicSize = Number(prevRow?.at(0) ?? 0);
    const maxCubicSize = Number(row.at(0) ?? 0);
    return { minCubicSize: prevMaxCubicSize, maxCubicSize };
  });
}

/** Appends a zero-filled row shaped like the grid's last row. */
export function addGridBottomRow(input: RateCardForm): void {
  const bottomRow = input.value.data.at(-1)?.map(() => "0") ?? [];
  input.inputs.data.setValue([...input.value.data, bottomRow]);
}

export function deleteGridRow(input: RateCardForm, index: number): void {
  input.inputs.data.setValue(input.value.data.filter((_, i) => i !== index));
}

// Input forms for rate tables

export const rateCardKeyForm = groupInput({
  selectedCarrier: input<RateTableCarrier>(),
  selectedServiceLevel: input<RateTableServiceLevel>(),
  valueMode: input<RateTableValueMode>(),
  rateTableType: input<OutboundRateTableType>(),
});

export type RateCardKeyForm = InputSchema.Input<typeof rateCardKeyForm>;
export type RateCardKeyFormValue = InputSchema.Value<typeof rateCardKeyForm>;
export const rateCardKeyFormDefault: RateCardKeyFormValue = {
  selectedCarrier: RateTableCarrier.USPS,
  selectedServiceLevel: "Default",
  valueMode: RateTableValueMode.Spread,
  rateTableType: RateTableType.OUTBOUND_WEIGHT_BY_ZONE,
};

export const rateCardForm = groupInput({
  defaultValue: input<string>(),
  defaultValuePercentage: input<number | undefined>(),
  useDefaultValuePercentage: input<boolean | undefined>(),
  data: listInput(
    () => inputRow,
    () => [],
    (_, index) => index,
  ),
});

export const inputRow = listInput(
  () => input<string>({ validator: nonEmptyValidator }),
  () => "",
  (_, index) => index,
);

export type RateCardForm = InputSchema.Input<typeof rateCardForm>;
export type RateCardValue = InputSchema.Value<typeof rateCardForm>;

export function getRateTableValueModeLabel(
  valueMode: RateTableValueMode,
): string {
  switch (valueMode) {
    case RateTableValueMode.Spread:
      return "Spread ($)";
    case RateTableValueMode.SpreadPercent:
      return "Spread (%)";
    case RateTableValueMode.Rate:
      return "Rate";
    case RateTableValueMode.Discount:
      return "Discount";
    default:
      return valueMode;
  }
}
